/**
 * FinminOS Button component.
 *
 * Variant map (matches FinminOS design system):
 *   default   → Amber fill   (primary CTA — "START YOUR JOURNEY", "VERIFY & SIGN IN")
 *   secondary → Forest fill, cream text
 *   outline   → Transparent, forest border + text (ghost)
 *   ghost     → Transparent, no border, subtle hover tint
 *   destructive → Red fill
 *   link      → Underline text link
 *
 * Buttons use ALL CAPS + wide tracking per brand guidelines.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const buttonVariants = cva(
  [
    // Base: layout, type, and interaction
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    // Typography: ALL CAPS + wide tracking per FinminOS brand
    'text-[13px] font-semibold uppercase tracking-widest font-body',
    // Radii: medium-soft (10px = --radius)
    'rounded-md',
    // Focus ring using amber accent
    'ring-offset-background transition-[filter,background-color]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    // Disabled
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        /**
         * Primary CTA — Amber fill (#FF8C03), white text.
         * Uses bg-accent which maps to --accent (Amber) — already in the config.
         */
        default:
          'bg-accent text-accent-foreground hover:brightness-[0.94] active:brightness-[0.88]',

        /**
         * Secondary — Forest fill (#0D4A45), Cream text.
         * Uses bg-primary which maps to --primary (Forest in light mode).
         * bg-forest is preferred long-term but requires a dev-server restart
         * after the palette token was added to preset.ts.
         */
        secondary:
          'bg-primary text-primary-foreground hover:brightness-[0.94] active:brightness-[0.88]',

        /**
         * Outline / ghost with border — transparent bg, forest border + text.
         */
        outline:
          'border border-input bg-transparent text-foreground hover:bg-foreground/[0.04]',

        /**
         * Ghost — no border, subtle hover bg.
         */
        ghost:
          'bg-transparent text-foreground hover:bg-foreground/[0.04]',

        /**
         * Destructive — red fill.
         */
        destructive:
          'bg-destructive text-destructive-foreground hover:brightness-[0.94]',

        /**
         * Text link — underline on hover.
         */
        link:
          'text-foreground underline-offset-4 hover:underline normal-case tracking-normal',
      },
      size: {
        default: 'h-11 px-7 py-3',   /* 44px height, generous padding */
        sm:      'h-9  px-4 py-2 text-xs',
        lg:      'h-12 px-9 py-3 text-sm',
        icon:    'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
